/**
 * Normalize a search query for dirty-checking against a saved-search baseline.
 * Array order and object identity are ignored; slider-null semantics match buildSearchQuery.
 */

const sortStrings = (values) =>
  [...(Array.isArray(values) ? values : [])]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right));

const normalizeBound = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/**
 * @param {object|null|undefined} query
 * @returns {object|null}
 */
export function normalizeSearchQueryForCompare(query) {
  if (!query || typeof query !== "object" || Array.isArray(query)) return null;

  const natural = String(query.NaturalLanguageQuery || "").trim();
  if (natural || query.OnlyNaturalLanguageQuery) {
    return {
      mode: "natural",
      NaturalLanguageQuery: natural,
    };
  }

  return {
    mode: "structured",
    TermsInclude: sortStrings(query.TermsInclude),
    TermsExclude: sortStrings(query.TermsExclude),
    IndustryInclude: sortStrings(query.IndustryInclude),
    IndustryExclude: sortStrings(query.IndustryExclude),
    Location: sortStrings(query.Location),
    RevenueLowerBound: normalizeBound(query.RevenueLowerBound),
    RevenueUpperBound: normalizeBound(query.RevenueUpperBound),
    EmployeesLowerBound: normalizeBound(query.EmployeesLowerBound),
    EmployeesUpperBound: normalizeBound(query.EmployeesUpperBound),
  };
}

export function areSearchQueriesEquivalent(left, right) {
  const normalizedLeft = normalizeSearchQueryForCompare(left);
  const normalizedRight = normalizeSearchQueryForCompare(right);
  if (normalizedLeft === null && normalizedRight === null) return true;
  if (normalizedLeft === null || normalizedRight === null) return false;
  return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
}

/**
 * Save Search enablement:
 * - always disabled when there is no active search
 * - with a loaded saved-search baseline (V4), disabled until applied filters diverge
 */
export function isSaveSearchDisabled({
  isSearchActive,
  hasSavedBaseline = false,
  isDirty = false,
  requireDirtyWhenSaved = false,
}) {
  if (!isSearchActive) return true;
  if (requireDirtyWhenSaved && hasSavedBaseline) return !isDirty;
  return false;
}

/**
 * Clear CTA visibility for the filter toolbar:
 * - with a saved-search baseline, only show after filters diverge (dirty)
 * - without a baseline, show whenever any manual filters are applied
 */
export function shouldShowClearFilters({
  hasAnyManualFilters = false,
  hasSavedBaseline = false,
  isDirty = false,
}) {
  if (hasSavedBaseline) return Boolean(isDirty);
  return Boolean(hasAnyManualFilters);
}
